import React, { useEffect, useState } from 'react';
import { fetchDealCostModel } from '../data/api';
import type { CostModelLine, CostModelSource, DealCostModel } from '../data/api';
import { MEDIA_ORIGIN } from '../auth/config';

// The deal as the cost model a producer reasons in: what each animal cost,
// what it earned, what was left -- and, on request, the invoices behind every
// line. Willem's own spreadsheet is a per-lamb model; this is the same shape
// filled with actuals, so he can read it without translating.

const zar = new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 2 });
const fmtMoney = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? '—' : zar.format(value);
const fmtNum = (value: number | null | undefined, digits = 1): string =>
  value === null || value === undefined || !Number.isFinite(value) ? '—' : value.toLocaleString('en-ZA', { maximumFractionDigits: digits, minimumFractionDigits: digits });
const fmtInt = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? '—' : Math.round(value).toLocaleString('en-ZA');

const mediaUrl = (url: string | null): string | null => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${MEDIA_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
};

const SOURCE_KIND_LABEL: Record<string, string> = {
  document: 'Document',
  wool_sale: 'Wool sale',
  medication: 'Medication',
  shared: 'Shared cost',
  journey: 'Journey',
  feedlot_invoice: 'Feedlot invoice',
  shearing_session: 'Shearing',
  estimate: 'Estimate',
  ledger: 'Ledger',
};

function SourceRow({ source }: { source: CostModelSource }) {
  const href = mediaUrl(source.documentUrl);
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 py-1.5 text-xs">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="rounded border border-[#1e6bb824] bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-avcs-muted">
            {SOURCE_KIND_LABEL[source.kind] || source.kind}
          </span>
          <span className="text-avcs-text">{source.label}</span>
          {source.reference && <span className="text-avcs-muted">Ref {source.reference}</span>}
          {source.date && <span className="text-avcs-muted">{source.date}</span>}
          {href && (
            <a className="font-semibold text-[#1e6bb8] hover:underline" href={href} target="_blank" rel="noreferrer">
              {source.documentName ? `Open ${source.documentName.toLowerCase()}` : 'Open document'}
            </a>
          )}
        </div>
        {source.detail && <div className="mt-0.5 text-avcs-muted">{source.detail}</div>}
      </div>
      <span className="tabular-nums text-avcs-text">{fmtMoney(source.amount)}</span>
    </li>
  );
}

function LineRow({
  line,
  animalCount,
  open,
  onToggle,
  tone,
}: {
  line: CostModelLine;
  animalCount: number;
  open: boolean;
  onToggle: () => void;
  tone: 'cost' | 'income';
}) {
  const hasSources = line.sources.length > 0;
  const empty = line.total === 0;
  return (
    <>
      <tr className={`border-b border-[#1e6bb814] ${empty ? 'text-avcs-muted' : 'text-avcs-text'}`}>
        <td className="px-3 py-2 text-sm">
          <div className="flex items-center gap-2">
            <span>{line.label}</span>
            {hasSources && (
              <button
                type="button"
                className="rounded-full border border-[#1e6bb824] bg-white px-2 py-0.5 text-[11px] font-semibold text-[#1e6bb8] hover:bg-[#f1f7ff]"
                onClick={onToggle}
                aria-expanded={open}
              >
                {open ? 'Hide' : `${line.sources.length} source${line.sources.length === 1 ? '' : 's'}`}
              </button>
            )}
          </div>
        </td>
        <td className="px-3 py-2 text-right text-sm tabular-nums">{fmtMoney(line.total)}</td>
        <td className={`px-3 py-2 text-right text-sm font-semibold tabular-nums ${empty ? '' : tone === 'income' ? 'text-[#106b38]' : 'text-avcs-text'}`}>
          {animalCount > 0 ? fmtMoney(line.perAnimal) : '—'}
        </td>
      </tr>
      {open && hasSources && (
        <tr className="border-b border-[#1e6bb814] bg-[#fbfeff]">
          <td colSpan={3} className="px-3 pb-2 pt-1">
            <ul className="divide-y divide-[#1e6bb814]">
              {line.sources.map((source, index) => (
                <SourceRow key={`${line.key}-${index}`} source={source} />
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

function LinesTable({
  title,
  lines,
  totalLabel,
  total,
  totalPerAnimal,
  animalCount,
  tone,
}: {
  title: string;
  lines: CostModelLine[];
  totalLabel: string;
  total: number;
  totalPerAnimal: number | null;
  animalCount: number;
  tone: 'cost' | 'income';
}) {
  const [openKeys, setOpenKeys] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setOpenKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  return (
    <div className="min-w-0 rounded-xl border border-[#1e6bb824] bg-white">
      <div className="admin-table-scroll overflow-x-auto">
        <table className="w-full min-w-[420px] border-collapse">
          <thead>
            <tr className="border-b border-[#1e6bb824] bg-[#fbfeff] text-left text-xs uppercase tracking-wide text-avcs-muted">
              <th className="px-3 py-2 font-semibold">{title}</th>
              <th className="px-3 py-2 text-right font-semibold">Total</th>
              <th className="px-3 py-2 text-right font-semibold">Per animal</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <LineRow key={line.key} line={line} animalCount={animalCount} open={Boolean(openKeys[line.key])} onToggle={() => toggle(line.key)} tone={tone} />
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-[#fbfeff]">
              <td className="px-3 py-2 text-sm font-semibold text-avcs-text">{totalLabel}</td>
              <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums text-avcs-text">{fmtMoney(total)}</td>
              <td className={`px-3 py-2 text-right text-sm font-semibold tabular-nums ${tone === 'income' ? 'text-[#106b38]' : 'text-avcs-text'}`}>
                {animalCount > 0 ? fmtMoney(totalPerAnimal) : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/**
 * Money per kilogram of carcass.
 *
 * All three cards divide by the SAME weight -- the cold carcass mass actually
 * weighed -- so they can be read against each other: income per kilo minus
 * total cost per kilo is the margin on a kilo. Giving each its own natural
 * denominator (purchase over live weight bought, income over carcass) would
 * make three numbers that cannot be compared, which is the one thing putting
 * them side by side promises.
 */
function PerKgCard({
  label, amount, carcassKg, numerator, tone = 'cost',
}: {
  label: string;
  amount: number | null;
  carcassKg: number | null;
  /** What the money is, so this card cannot be confused with a neighbour that
   *  divides a different total by the same weight -- "Carcass income per kg"
   *  below is carcass SALES only, while "Income per kilo" here is every income
   *  line, wool included. On a deal with a wool clip the two differ, and the
   *  reader has to be able to see why without doing the arithmetic. */
  numerator: string;
  tone?: 'cost' | 'income';
}) {
  const value = carcassKg && carcassKg > 0 && amount !== null ? amount / carcassKg : null;
  return (
    <div className="rounded-xl border border-[#1e6bb829] bg-white p-3">
      <div className="text-[11px] uppercase tracking-wide text-avcs-muted">{label}</div>
      <div className={`mt-0.5 text-lg font-semibold tabular-nums ${tone === 'income' ? 'text-[#106b38]' : 'text-avcs-text'}`}>
        {value === null ? '—' : `${fmtMoney(value)} / kg`}
      </div>
      <div className="mt-0.5 text-xs text-avcs-muted">
        {carcassKg && carcassKg > 0
          ? `${numerator} ${fmtMoney(amount ?? 0)} ÷ ${fmtNum(carcassKg, 1)} kg carcass`
          : 'no carcasses weighed yet'}
      </div>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-[#1e6bb829] bg-white p-3">
      <div className="text-[11px] uppercase tracking-wide text-avcs-muted">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums text-avcs-text">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-avcs-muted">{hint}</div>}
    </div>
  );
}

/**
 * The shape both a single deal's cost model and the programme-level pool share.
 * `sources` is optional because the pooled lines carry none -- there is no one
 * invoice behind a figure summed across five deals -- and `LinesTable` simply
 * shows no "sources" button for such a line.
 */
export type CostModelViewData = {
  animalCount: number;
  animals: { active: number; slaughtered: number; sold: number; deceased: number };
  totals: DealCostModel['totals'];
  costLines: Array<Omit<CostModelLine, 'sources'> & { sources?: CostModelSource[] }>;
  incomeLines: Array<Omit<CostModelLine, 'sources'> & { sources?: CostModelSource[] }>;
  // Two fields exist only on a single deal. A daily feedlot rate pooled across
  // several feedlots is not one number, and the batch-live-mass provenance is
  // per carcass record. The view shows a dash for the first and drops the
  // provenance clause for the second rather than pretending to a pooled value.
  performance: Omit<DealCostModel['performance'], 'dailyFeedlotFeePerAnimal' | 'carcassesFromBatchLiveMass'> & {
    dailyFeedlotFeePerAnimal?: number | null;
    carcassesFromBatchLiveMass?: number;
  };
};

/**
 * Fetches one deal's model and renders it. The rendering itself lives in
 * `CostModelView` so the programme-analysis report can show the identical
 * layout for a pool of deals -- same cards, same tables, same per-kilo figures
 * -- without a second copy of the markup that would drift from this one.
 */
export default function DealCostModel({ dealId, refreshToken = 0 }: { dealId: string; refreshToken?: number }) {
  const [model, setModel] = useState<DealCostModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchDealCostModel(dealId)
      .then((data) => { if (!cancelled) setModel(data); })
      .catch((err: any) => { if (!cancelled) setError(err?.message || 'Could not load the margin model.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dealId, refreshToken]);

  if (loading && !model) return <p className="text-sm text-avcs-muted">Loading margin model…</p>;
  if (error) return <div className="rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{error}</div>;
  if (!model) return null;
  return <CostModelView model={model} />;
}

export function CostModelView({ model }: { model: CostModelViewData }) {
  const { totals, performance: perf, animalCount } = model;
  // Lines without sources are pooled lines; give LineRow an empty list so its
  // "N sources" affordance stays hidden rather than crashing on undefined.
  const costLines: CostModelLine[] = model.costLines.map((l) => ({ ...l, sources: l.sources ?? [] }));
  const incomeLines: CostModelLine[] = model.incomeLines.map((l) => ({ ...l, sources: l.sources ?? [] }));
  const isLoss = totals.margin < 0;
  const closedOut = model.animals.slaughtered + model.animals.sold + model.animals.deceased;
  const stillOpen = animalCount - closedOut;

  return (
    <div className="grid gap-4">
      {/* Headline: the three numbers Willem reads first, per animal. */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-[#1e6bb829] bg-white p-4">
          <div className="tile-label">Cost per animal</div>
          <div className="tile-value">{fmtMoney(totals.costPerAnimal)}</div>
          <div className="mt-1 text-xs text-avcs-muted">{fmtMoney(totals.cost)} across {fmtInt(animalCount)} animals</div>
        </div>
        <div className="rounded-xl border border-[#1e6bb829] bg-white p-4">
          <div className="tile-label">Income per animal</div>
          <div className="tile-value text-[#106b38]">{fmtMoney(totals.incomePerAnimal)}</div>
          <div className="mt-1 text-xs text-avcs-muted">{fmtMoney(totals.income)} booked so far</div>
        </div>
        <div className={`rounded-xl border p-4 ${isLoss ? 'border-[#e7bbb9] bg-[#fdf1ef]' : 'border-[#b9e7c9] bg-[#f0faf4]'}`}>
          <div className="tile-label">{isLoss ? 'Loss per animal' : 'Margin per animal'}</div>
          <div className={`tile-value ${isLoss ? 'text-[#6b1010]' : 'text-[#106b38]'}`}>{fmtMoney(totals.marginPerAnimal)}</div>
          <div className="mt-1 text-xs text-avcs-muted">
            {fmtMoney(totals.margin)} total{totals.marginPercent !== null ? ` · ${fmtNum(totals.marginPercent, 1)}% of income` : ''}
          </div>
        </div>
      </div>

      {stillOpen > 0 && (
        <p className="text-xs text-avcs-muted">
          {fmtInt(stillOpen)} of {fmtInt(animalCount)} animals are still active, so income is partial and the feedlot fee is still accruing. The per-animal figures divide by all {fmtInt(animalCount)}.
        </p>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        <LinesTable title="Costs" lines={costLines} totalLabel="Total cost" total={totals.cost} totalPerAnimal={totals.costPerAnimal} animalCount={animalCount} tone="cost" />
        <div className="grid min-w-0 gap-3 self-start">
          <LinesTable title="Income" lines={incomeLines} totalLabel="Total income" total={totals.income} totalPerAnimal={totals.incomePerAnimal} animalCount={animalCount} tone="income" />
          {/* Carcass mass is not on the payload, but avg × count is exactly it:
              the average is that total over that count. */}
          {(() => {
            const carcassKg = perf.avgCarcassWeightKg !== null && perf.carcassesWeighed
              ? perf.avgCarcassWeightKg * perf.carcassesWeighed
              : null;
            const purchase = costLines.find((l) => l.key === 'purchase')?.total ?? null;
            return (
              <div className="grid gap-3 sm:grid-cols-3">
                <PerKgCard label="Purchase cost per kilo" amount={purchase} carcassKg={carcassKg} numerator="purchase price" />
                <PerKgCard label="Total cost per kilo" amount={totals.cost} carcassKg={carcassKg} numerator="all costs" />
                <PerKgCard label="Income per kilo" amount={totals.income} carcassKg={carcassKg} numerator="all income" tone="income" />
              </div>
            );
          })()}
        </div>
      </div>

      <div>
        <h4 className="mb-2 text-sm font-semibold text-avcs-text">Performance behind the numbers</h4>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Days on feed" value={fmtNum(perf.avgDaysInFeedlot, 1)} hint="average per animal" />
          <Stat label="Average daily gain" value={perf.avgAdgKg === null ? '—' : `${fmtNum(perf.avgAdgKg, 3)} kg`} hint={perf.weightGainPerAnimalKg === null ? undefined : `${fmtNum(perf.weightGainPerAnimalKg, 1)} kg gained per animal`} />
          <Stat
            label="Feed conversion"
            value={perf.fcr !== null ? fmtNum(perf.fcr, 2) : perf.fcrEstimated !== null ? `${fmtNum(perf.fcrEstimated, 2)} (est.)` : '—'}
            hint={perf.fcr !== null ? 'kg feed per kg gain, measured' : perf.fcrEstimated !== null ? 'estimated from body weight — no feed kilograms allocated yet' : undefined}
          />
          <Stat label="Feed per animal" value={perf.feedPerAnimalKg === null ? '—' : `${fmtNum(perf.feedPerAnimalKg, 1)} kg`} hint={perf.feedCostPerKg === null ? undefined : `${fmtMoney(perf.feedCostPerKg)} per kg feed`} />
          <Stat label="Feed cost per kg gain" value={fmtMoney(perf.feedCostPerKgGain)} />
          <Stat
            label="Feedlot fee"
            value={perf.dailyFeedlotFeePerAnimal === null || perf.dailyFeedlotFeePerAnimal === undefined ? '—' : `${fmtMoney(perf.dailyFeedlotFeePerAnimal)} / day`}
            hint={perf.dailyFeedlotFeePerAnimal === undefined ? 'per feedlot; not one rate across a pool' : 'per animal, from the feedlot entity'}
          />
          <Stat
            label="Dressing %"
            value={perf.dressingPercentage === null ? '—' : `${fmtNum(perf.dressingPercentage, 1)}%`}
            hint={
              perf.carcassesWeighed
                ? `${fmtInt(perf.carcassesWeighed)} carcasses, avg ${fmtNum(perf.avgCarcassWeightKg, 2)} kg` +
                  (perf.carcassesFromBatchLiveMass === undefined
                    ? ''
                    : perf.carcassesFromBatchLiveMass >= perf.carcassesWeighed
                      ? ' · from the abattoir batch live mass'
                      : perf.carcassesFromBatchLiveMass > 0
                        ? ` · ${fmtInt(perf.carcassesFromBatchLiveMass)} from the abattoir batch live mass, the rest from the last feedlot weigh`
                        : ' · from each animal\'s last feedlot weigh, not the abattoir')
                : 'no measured carcasses yet'
            }
          />
          <Stat label="Carcass income per kg" value={fmtMoney(perf.carcassIncomePerKg)} hint="sales ÷ cold carcass mass" />
        </div>
      </div>
    </div>
  );
}
